import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";
import { saveOrendaAuth, clearOrendaAuth } from "./orendaAuthStorage";

// Cognito quirk: the cognito-idp issuer's OpenID discovery document publishes
// JWKS + userinfo but NOT authorization_endpoint / token_endpoint /
// end_session_endpoint (those live under the hosted-UI domain). We fetch the
// discovery doc for JWKS/issuer, then override the endpoints so sign-in /
// sign-out actually work.
const HOSTED_UI = "https://bopayments.auth.eu-west-1.amazoncognito.com";

// Singleton UserManager for the Cognito flow. Import this anywhere in the app
// that needs to trigger sign-in, read the current user, or sign out.
export const orendaUserManager = new UserManager({
  authority: orendaOidcConfig.authority,
  client_id: orendaOidcConfig.client_id,
  redirect_uri: orendaOidcConfig.redirect_uri,
  post_logout_redirect_uri: orendaOidcConfig.post_logout_redirect_uri,
  response_type: orendaOidcConfig.response_type,
  scope: orendaOidcConfig.scope,
  metadataUrl: orendaOidcConfig.metadataUrl,
  metadataSeed: {
    authorization_endpoint: `${HOSTED_UI}/oauth2/authorize`,
    token_endpoint: `${HOSTED_UI}/oauth2/token`,
    userinfo_endpoint: `${HOSTED_UI}/oauth2/userInfo`,
    end_session_endpoint: `${HOSTED_UI}/logout`,
    revocation_endpoint: `${HOSTED_UI}/oauth2/revoke`,
  },
  // Popup flow closes this page after the callback fires.
  popup_redirect_uri: orendaOidcConfig.redirect_uri,
  popup_post_logout_redirect_uri: orendaOidcConfig.post_logout_redirect_uri,
  // Cache tokens in sessionStorage (matches existing Card-Console pattern).
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: orendaOidcConfig.automaticSilentRenew,
  // Fire the accessTokenExpiring event 60s before expiry so we can silently
  // renew (default is 60s; being explicit for clarity).
  accessTokenExpiringNotificationTimeInSeconds: 60,
});

// Silent renewal glue.
// When oidc-client-ts refreshes the token, `addUserLoaded` fires with the new
// User object. We mirror the new tokens into our sessionStorage.orendaAuth
// entry so every service call (which reads via readOrendaAuth) sees the fresh
// value. If refresh fails (refresh_token expired, network, etc.) we clear the
// session and let the landing page kick off a fresh signinRedirect.
orendaUserManager.events.addUserLoaded((user) => {
  saveOrendaAuth({
    id_token: user.id_token,
    access_token: user.access_token,
    refresh_token: user.refresh_token,
    expires_in: user.expires_in,
    token_type: user.token_type,
    scope: user.scope,
  });
});

orendaUserManager.events.addSilentRenewError((err) => {
  // Do not clear here — let the landing page's session-status check drive the
  // "sign in again" UI. Just log so we can see it in the console.
  // eslint-disable-next-line no-console
  console.warn("[orenda] silent renew failed:", err?.message || err);
});

orendaUserManager.events.addUserSignedOut(() => {
  clearOrendaAuth();
});

// Hard logout: hits Cognito's hosted-UI /logout endpoint directly, which
// clears the *Cognito* session cookie (the piece MSAL/oidc-client can't touch
// because it lives on the cognito domain). Without this, Cognito silently
// reuses the last-signed-in account on next /oauth2/authorize call —
// prompt=login doesn't reliably override it for federated IdPs.
//
// Flow: clear local storage → navigate to Cognito /logout → Cognito clears
// its cookie → redirects to logout_uri (our /orenda/logout page) → user is
// back on our site with a clean slate.
//
// We flip isSigningOut() to true for the duration of the navigation so other
// components (OrendaLanding, service calls) can suppress error UI that would
// otherwise flash — e.g. an in-flight viewapplication racing with the logout
// returns 401 and would render a "Something went wrong" screen before the
// browser navigates. Checking this flag lets the UI stay quiet instead.
let _isSigningOut = false;
export const isSigningOut = () => _isSigningOut;

// Decode the `tid` (Entra tenant id) claim from the stored id_token.
// Cognito-federated users have Entra's tenant GUID nested inside the
// token's `identities[0].issuer` field; some tokens also carry it as
// a direct `tid` claim. We try both; fall back to "common" if neither
// is present (Entra still processes a common-tenant logout).
const decodeEntraTenantFromIdToken = () => {
  try {
    const raw = window.sessionStorage.getItem("orendaAuth");
    if (!raw) return null;
    const auth = JSON.parse(raw);
    const idToken = auth?.id_token;
    if (!idToken) return null;
    const payload = idToken.split(".")[1];
    if (!payload) return null;
    const normalised = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(normalised));
    if (claims?.tid && /^[0-9a-f-]{10,}$/i.test(claims.tid)) return claims.tid;
    const ident = Array.isArray(claims?.identities) && claims.identities[0];
    const issuer = ident?.issuer || "";
    const m = issuer.match(/\/([0-9a-f-]{10,})\/?$/i);
    if (m) return m[1];
    return null;
  } catch {
    return null;
  }
};

// Mark the user as "intentionally signed out" so the next arrival at
// /orenda-landing in this browser forces re-authentication at EVERY
// layer — Cognito session, Entra federated session, device-bound SSO.
// A per-browser localStorage flag survives the whole sign-out chain
// (sessionStorage would be wiped en-route) and gets consumed on the
// next sign-in.
export const FORCE_REAUTH_FLAG = "orendaForceReauth";
const markForceReauth = () => {
  try { window.localStorage.setItem(FORCE_REAUTH_FLAG, "1"); } catch { /* ignore */ }
};
export const consumeForceReauthFlag = () => {
  try {
    const v = window.localStorage.getItem(FORCE_REAUTH_FLAG);
    if (v) {
      window.localStorage.removeItem(FORCE_REAUTH_FLAG);
      return true;
    }
  } catch { /* ignore */ }
  return false;
};

// Store the next step of the logout chain so a return visit from Entra's
// "You signed out" page knows to continue to Cognito instead of showing
// the dashboard. The flag lives in localStorage because sessionStorage
// gets cleared below AND survives the cross-origin bounce to Entra and
// back.
const LOGOUT_STAGE_KEY = "orendaLogoutStage";
const setLogoutStage = (stage) => {
  try { window.localStorage.setItem(LOGOUT_STAGE_KEY, stage); } catch { /* ignore */ }
};
export const getLogoutStage = () => {
  try { return window.localStorage.getItem(LOGOUT_STAGE_KEY); } catch { return null; }
};
export const clearLogoutStage = () => {
  try { window.localStorage.removeItem(LOGOUT_STAGE_KEY); } catch { /* ignore */ }
};

// Build Cognito's logout URL — where we send the user AFTER Entra.
const buildCognitoLogoutUrl = () => {
  const u = new URL(`${HOSTED_UI}/logout`);
  u.searchParams.set("client_id", orendaOidcConfig.client_id);
  u.searchParams.set("logout_uri", orendaOidcConfig.post_logout_redirect_uri);
  return u.toString();
};

// Full sign-out — top-level navigations with a resume flag between them,
// because Entra ignores post_logout_redirect_uri when the redirect URL
// isn't registered on the Entra app registration (which it isn't for
// this project). We work around that by:
//
//   1. Clear local storage + Redux user + oidc-client state
//   2. Set LOGOUT_STAGE_KEY = "cognito-pending" in localStorage
//   3. Set FORCE_REAUTH_FLAG so the NEXT sign-in uses max_age=0
//   4. Navigate the whole tab to Entra's /logout
//   5. Entra shows "You signed out of your account" — user closes nothing
//   6. The user clicks the app again (or we auto-redirect from Entra's
//      page — see 7)
//   7. On ANY mount of our root components, we check LOGOUT_STAGE_KEY
//      and if it's "cognito-pending" we immediately navigate to
//      Cognito's /logout, which then bounces to /orenda/logout → /login.
//
// Point 7 is implemented in App.js (or main index.js) so even landing
// anywhere in our app during an interrupted logout resumes the chain.
//
// For the "show Entra's page briefly then auto-continue" UX, we also
// expose continueLogoutChain() which the app can call unconditionally on
// mount — safe to call when the flag is absent (becomes a no-op).
export const hardLogoutOrenda = () => {
  if (_isSigningOut) return;
  _isSigningOut = true;

  // Decode tenant BEFORE wiping storage — id_token lives in orendaAuth.
  const tenant = decodeEntraTenantFromIdToken() || "common";

  // Clear local session + flags.
  try {
    window.sessionStorage.removeItem("orendaAuth");
    window.sessionStorage.removeItem("orendaCard");
    window.sessionStorage.removeItem("user");
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith("oidc.")) window.sessionStorage.removeItem(key);
    }
  } catch { /* ignore */ }
  markForceReauth();
  // Tell the next mount of our app to continue to Cognito's logout.
  setLogoutStage("cognito-pending");

  // Navigate to Entra. Pass post_logout_redirect_uri anyway in case the
  // tenant has it whitelisted — if so Entra redirects to Cognito straight
  // from here (even better). If not, the user lands on Entra's "signed
  // out" page; next time they hit our app the resume flag kicks in.
  const entraLogoutUrl = new URL(
    `https://login.microsoftonline.com/${encodeURIComponent(tenant)}/oauth2/v2.0/logout`
  );
  entraLogoutUrl.searchParams.set(
    "post_logout_redirect_uri",
    buildCognitoLogoutUrl()
  );

  window.location.replace(entraLogoutUrl.toString());
};

// Call this unconditionally on app mount. If the user is mid-logout
// (Entra done, Cognito pending), it navigates to Cognito's /logout which
// then bounces to /orenda/logout → /login. Returns true if it navigated
// away, false if nothing to do (so callers can short-circuit).
export const continueLogoutChain = () => {
  const stage = getLogoutStage();
  if (stage === "cognito-pending") {
    // Move the stage forward before we leave so a reload during the hop
    // doesn't loop. The OrendaLogout page clears the flag entirely once
    // we land there.
    setLogoutStage("cognito-in-flight");
    window.location.replace(buildCognitoLogoutUrl());
    return true;
  }
  return false;
};
